/**
 * SmartActionButton Component
 * Handles all action button states based on user membership and program status
 * Extracted from ClubDetails.js lines 1517-1646
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaInfoCircle } from 'react-icons/fa';
import RegistrationButton from '../../../../components/Registration/RegistrationButton';
import { useProgramMutations } from '../../../../hooks/useProgramsApi';
import { isRegistrationClosed } from '../../utils/programHelpers';
import styles from './SmartActionButton.module.css';

const SmartActionButton = ({
  program,
  clubId,
  myMembershipStatus,
  isClubManager,
  enrolledCount = 0,
  onJoinClubRequired,
  onRegistrationComplete,
  onMembershipStatusChange,
  refreshMembershipState,
  size = 'medium', // 'small' | 'medium' | 'large'
  showDetailsButton = true, // Hide on details page
  showNotifications = false // Only show toasts on single-program views
}) => {
  const navigate = useNavigate();
  const programMutations = useProgramMutations(program?._id);

  if (!program) return null;

  // Calculate capacity status
  const maxCapacity = program?.capacity?.maxParticipants || program?.maxCapacity || program?.maxPlayers;
  const atCapacity = maxCapacity ? enrolledCount >= Number(maxCapacity) : false;
  const registrationClosed = isRegistrationClosed(program);

  // Button size classes
  const sizeClass = styles[size] || styles.medium;

  // Navigate to program details page
  const handleViewDetails = (e) => {
    e.stopPropagation();
    const targetClubId = clubId || program?.clubId?._id || program?.clubId || program?.club?._id || program?.club;
    if (targetClubId && program?._id) {
      navigate(`/clubs/${targetClubId}/programs/${program._id}`);
    }
  };

  // Render details button
  const renderDetailsButton = () => {
    if (!showDetailsButton) return null;
    return (
      <button
        className={styles.detailsButton}
        onClick={handleViewDetails}
        title="View Program Details"
      >
        <FaInfoCircle />
        <span>Details</span>
      </button>
    );
  };

  /**
   * CASE 1: Club Manager - Always "Enter"
   */
  if (isClubManager) {
    return (
      <div className={`${styles.buttonContainer} ${sizeClass}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.buttonRow}>
          <button 
            className={styles.enterButton}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/program/${program._id}`);
            }}
          >
            Enter <span className={styles.arrow}>→</span>
          </button>
          {renderDetailsButton()}
        </div>
      </div>
    );
  }

  /**
   * CASE 2: Enrolled User - "Enter"
   */
  if (myMembershipStatus === 'Enrolled') {
    return (
      <div className={`${styles.buttonContainer} ${sizeClass}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.buttonRow}>
          <button 
            className={styles.enterButton}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/program/${program._id}`);
            }}
          >
            Enter <span className={styles.arrow}>→</span>
          </button>
          {renderDetailsButton()}
        </div>
      </div>
    );
  }

  /**
   * CASE 3: Applied User - Show RegistrationButton
   */
  if (myMembershipStatus === 'Applied') {
    return (
      <div className={sizeClass} onClick={(e) => e.stopPropagation()}>
        <RegistrationButton
          program={program}
          clubId={clubId}
          showDetailsButton={showDetailsButton}
          showNotifications={showNotifications}
          onRegistrationComplete={async () => {
            try {
              if (refreshMembershipState) await refreshMembershipState();
            } catch (err) {
              console.error('Error refreshing state:', err);
            }
          }}
        />
      </div>
    );
  }

  /**
   * CASE 4: Waitlisted User - Show RegistrationButton with canApplyDespiteRestrictions
   */
  if (myMembershipStatus === 'Waitlisted') {
    return (
      <div className={sizeClass} onClick={(e) => e.stopPropagation()}>
        <RegistrationButton
          program={program}
          clubId={clubId}
          showDetailsButton={showDetailsButton}
          showNotifications={showNotifications}
          onRegistrationComplete={async () => {
            try {
              if (refreshMembershipState) await refreshMembershipState();
            } catch (err) {
              console.error('Error refreshing state:', err);
            }
          }}
        />
      </div>
    );
  }

  /**
   * CASE 5: At Capacity or Registration Closed - "Join Waitlist"
   */
  if (atCapacity || registrationClosed) {
    return (
      <div className={`${styles.buttonContainer} ${sizeClass}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.buttonRow}>
          <button
            className={styles.waitlistButton}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await programMutations.waitlist();
                if (refreshMembershipState) await refreshMembershipState();
                if (onMembershipStatusChange) onMembershipStatusChange('Waitlisted');
              } catch (err) {
                console.error('Waitlist failed', err);
              }
            }}
          >
            Join Waitlist
          </button>
          {renderDetailsButton()}
        </div>
      </div>
    );
  }

  /**
   * CASE 6: Default - Smart RegistrationButton
   */
  return (
    <div className={sizeClass} onClick={(e) => e.stopPropagation()}>
      <RegistrationButton
        program={program}
        clubId={clubId}
        showDetailsButton={showDetailsButton}
        showNotifications={showNotifications}
        onRegistrationComplete={async () => {
          try {
            if (onRegistrationComplete) {
              await onRegistrationComplete();
            }
            if (refreshMembershipState) await refreshMembershipState();
          } catch (err) {
            console.error('Error completing registration:', err);
          }
        }}
        onJoinClubRequired={onJoinClubRequired}
      />
    </div>
  );
};

export default SmartActionButton;










